import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";

const Calculator = () => {
  const [display, setDisplay] = useState("0");
  const [previousValue, setPreviousValue] = useState(null);
  const [operation, setOperation] = useState(null);
  const [newNumber, setNewNumber] = useState(true);

  const handleNumber = (num) => {
    if (newNumber) {
      setDisplay(num.toString());
      setNewNumber(false);
    } else {
      setDisplay(display === "0" ? num.toString() : display + num);
    }
  };

  const handleDecimal = () => {
    if (newNumber) {
      setDisplay("0.");
      setNewNumber(false);
    } else if (!display.includes(".")) {
      setDisplay(display + ".");
    }
  };

  const handleOperation = (op) => {
    const current = parseFloat(display);
    
    if (previousValue === null) {
      setPreviousValue(current);
    } else if (operation) {
      const result = calculate(previousValue, current, operation);
      setDisplay(result.toString());
      setPreviousValue(result);
    }
    
    setOperation(op);
    setNewNumber(true);
  };

  const calculate = (a, b, op) => {
    let result;
    switch (op) {
      case "+":
        result = a + b;
        break;
      case "-":
        result = a - b;
        break;
      case "×":
        result = a * b;
        break;
      case "÷":
        result = b === 0 ? 0 : a / b;
        break;
      case "%":
        result = (a * b) / 100;
        break;
      default:
        result = b;
    }
    // Ondalık hassasiyet: kayan nokta hatalarını temizle (0.1+0.2 -> 0.3)
    return parseFloat(result.toFixed(8));
  };

  const handleEquals = () => {
    if (operation && previousValue !== null) {
      const current = parseFloat(display);
      const result = calculate(previousValue, current, operation);
      setDisplay(result.toString());
      setPreviousValue(null);
      setOperation(null);
      setNewNumber(true);
    }
  };

  const handleClear = () => {
    setDisplay("0");
    setPreviousValue(null);
    setOperation(null);
    setNewNumber(true);
  };

  const handleBackspace = () => {
    if (display.length > 1) {
      setDisplay(display.slice(0, -1));
    } else {
      setDisplay("0");
      setNewNumber(true);
    }
  };

  const formatDisplay = (val) => {
    const s = val.toString();
    const [intPart, decPart] = s.split(".");
    const neg = intPart.startsWith("-");
    const digits = neg ? intPart.slice(1) : intPart;
    const grouped = digits.replace(/\B(?=(\d{3})+(?!\d))/g, ".");
    let out = (neg ? "-" : "") + grouped;
    if (decPart !== undefined) out += "," + decPart;
    return out;
  };

  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button
          className="fixed bottom-4 left-4 w-12 h-12 md:bottom-6 md:right-6 md:left-auto md:w-16 md:h-16 rounded-full bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 shadow-2xl hover:shadow-purple-500/50 z-50"
          data-testid="calculator-trigger"
        >
          <svg className="w-6 h-6 md:w-8 md:h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
          </svg>
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="text-center text-xl">Hesap Makinesi</DialogTitle>
        </DialogHeader>
        <Card className="border-0 shadow-none">
          <CardContent className="p-4 space-y-3">
            {/* Display */}
            <div className="bg-slate-900 text-white p-6 rounded-xl text-right">
              <div className="text-sm text-slate-400 mb-1 h-4">
                {previousValue !== null && operation ? `${formatDisplay(previousValue)} ${operation}` : ""}
              </div>
              <div className="text-3xl font-bold truncate" data-testid="calculator-display">
                {formatDisplay(display)}
              </div>
            </div>

            {/* Buttons */}
            <div className="grid grid-cols-4 gap-2">
              {/* Row 1 */}
              <Button
                onClick={handleClear}
                className="bg-red-500 hover:bg-red-600 text-white font-semibold text-lg h-14 rounded-xl"
                data-testid="calc-clear"
              >
                C
              </Button>
              <Button
                onClick={handleBackspace}
                className="bg-slate-200 hover:bg-slate-300 text-slate-900 font-semibold text-lg h-14 rounded-xl"
                data-testid="calc-backspace"
              >
                ←
              </Button>
              <Button
                onClick={() => handleOperation("%")}
                className="bg-slate-200 hover:bg-slate-300 text-slate-900 font-semibold text-lg h-14 rounded-xl"
              >
                %
              </Button>
              <Button
                onClick={() => handleOperation("÷")}
                className="bg-amber-500 hover:bg-amber-600 text-white font-semibold text-lg h-14 rounded-xl"
              >
                ÷
              </Button>

              {/* Row 2 */}
              <Button
                onClick={() => handleNumber(7)}
                className="bg-white hover:bg-slate-50 text-slate-900 font-semibold text-lg h-14 rounded-xl border border-slate-200"
                data-testid="calc-7"
              >
                7
              </Button>
              <Button
                onClick={() => handleNumber(8)}
                className="bg-white hover:bg-slate-50 text-slate-900 font-semibold text-lg h-14 rounded-xl border border-slate-200"
                data-testid="calc-8"
              >
                8
              </Button>
              <Button
                onClick={() => handleNumber(9)}
                className="bg-white hover:bg-slate-50 text-slate-900 font-semibold text-lg h-14 rounded-xl border border-slate-200"
                data-testid="calc-9"
              >
                9
              </Button>
              <Button
                onClick={() => handleOperation("×")}
                className="bg-amber-500 hover:bg-amber-600 text-white font-semibold text-lg h-14 rounded-xl"
              >
                ×
              </Button>

              {/* Row 3 */}
              <Button
                onClick={() => handleNumber(4)}
                className="bg-white hover:bg-slate-50 text-slate-900 font-semibold text-lg h-14 rounded-xl border border-slate-200"
                data-testid="calc-4"
              >
                4
              </Button>
              <Button
                onClick={() => handleNumber(5)}
                className="bg-white hover:bg-slate-50 text-slate-900 font-semibold text-lg h-14 rounded-xl border border-slate-200"
                data-testid="calc-5"
              >
                5
              </Button>
              <Button
                onClick={() => handleNumber(6)}
                className="bg-white hover:bg-slate-50 text-slate-900 font-semibold text-lg h-14 rounded-xl border border-slate-200"
                data-testid="calc-6"
              >
                6
              </Button>
              <Button
                onClick={() => handleOperation("-")}
                className="bg-amber-500 hover:bg-amber-600 text-white font-semibold text-lg h-14 rounded-xl"
              >
                -
              </Button>

              {/* Row 4 */}
              <Button
                onClick={() => handleNumber(1)}
                className="bg-white hover:bg-slate-50 text-slate-900 font-semibold text-lg h-14 rounded-xl border border-slate-200"
                data-testid="calc-1"
              >
                1
              </Button>
              <Button
                onClick={() => handleNumber(2)}
                className="bg-white hover:bg-slate-50 text-slate-900 font-semibold text-lg h-14 rounded-xl border border-slate-200"
                data-testid="calc-2"
              >
                2
              </Button>
              <Button
                onClick={() => handleNumber(3)}
                className="bg-white hover:bg-slate-50 text-slate-900 font-semibold text-lg h-14 rounded-xl border border-slate-200"
                data-testid="calc-3"
              >
                3
              </Button>
              <Button
                onClick={() => handleOperation("+")}
                className="bg-amber-500 hover:bg-amber-600 text-white font-semibold text-lg h-14 rounded-xl"
              >
                +
              </Button>

              {/* Row 5 */}
              <Button
                onClick={() => handleNumber(0)}
                className="col-span-2 bg-white hover:bg-slate-50 text-slate-900 font-semibold text-lg h-14 rounded-xl border border-slate-200"
                data-testid="calc-0"
              >
                0
              </Button>
              <Button
                onClick={handleDecimal}
                className="bg-white hover:bg-slate-50 text-slate-900 font-semibold text-lg h-14 rounded-xl border border-slate-200"
                data-testid="calc-decimal"
              >
                ,
              </Button>
              <Button
                onClick={handleEquals}
                className="bg-gradient-to-r from-teal-500 to-emerald-500 hover:from-teal-600 hover:to-emerald-600 text-white font-semibold text-lg h-14 rounded-xl"
                data-testid="calc-equals"
              >
                =
              </Button>
            </div>
          </CardContent>
        </Card>
      </DialogContent>
    </Dialog>
  );
};

export default Calculator;
